import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import DocumentViewerModal from 'src/components/document-viewer/DocumentViewerModal';
import Loader from 'src/components/loader';
import useDocuments from 'src/hooks/services/useDocuments';
import { useUsersByUri } from 'src/hooks/services/useUser';
import { api } from 'src/utils/api';
import { API_RESOURCES, PRINT_STATUSES_TO_VARIANT } from 'src/utils/constants';
import { getUuid } from 'src/utils/url';

import WorkflowItem from './_workflow-item';

const WorkflowStep = ({ status, stepID, index, checkListLinkings, report }) => {
  const { addToast } = useToasts();

  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(null);
  const [workstationName, setWorkstationName] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  const workChecklist = checkListLinkings?.work_checklist;
  const workInstructions = useMemo(
    () => workChecklist?.work_instructions ?? [],
    [workChecklist?.work_instructions]
  );
  
  const latestReportsByInstruction = useMemo(() => {
    const map = {};

    workInstructions?.forEach((instruction) => {
      const filteredReports = report?.filter(
        (r) => r.work_instruction === instruction.uuid
      ) || [];

      const latest = filteredReports.reduce((latest, current) => {
        const currentDate = new Date(current.completed);
        const latestDate = latest ? new Date(latest.completed) : null;

        return !latest || currentDate > latestDate ? current : latest;
      }, null);

      if (latest) {
        map[instruction.uuid] = latest;
      }
    });

    return map;
  }, [workInstructions, report]);

  const { userUris, documentUris } = useMemo(() => {
    const users = new Set();
    const documents = new Set();

    Object.values(latestReportsByInstruction).forEach((instructionReport) => {
      if (instructionReport?.user) users.add(instructionReport.user);
      if (instructionReport?.report_file?.uri) documents.add(instructionReport.report_file.uri);
    });

    return {
      userUris: Array.from(users),
      documentUris: Array.from(documents),
    };
  }, [latestReportsByInstruction]);


  const { data: userList = {} } = useUsersByUri(userUris);
  const { documentList } = useDocuments(documentUris);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const step = await api.get(`${API_RESOURCES.PROCESS_STEP}/${stepID}`).json();
        if (step.workstation) {
          const workstepType = step.workstation.split('/')[3];
          const workStepEndpoints = {
            [API_RESOURCES.PRINTER]: API_RESOURCES.PRINTER,
            [API_RESOURCES.SHIPPING]: API_RESOURCES.SHIPPING,
            default: API_RESOURCES.POST_PROCESSOR,
          };
          const workStationEndpoint = workStepEndpoints[workstepType] || workStepEndpoints.default;

          const workstation = await api.get(
            `${workStationEndpoint}/${getUuid(step.workstation)}/`
          ).json();
          setWorkstationName(workstation.name);
        } else {
          setWorkstationName(step.name);
        }
        setStep(step);
      } catch (error) {
        console.error(error);
        addToast('There was an error. Please try again.', { appearance: 'error' });
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  const toggleDetails = () => {
    if (workInstructions.length > 0) {
      setShowDetails(prev => !prev);
    }
  };

  if (!step && isLoading) {
    return <Loader inline />;
  }

  if (!step) return null;

  const stepNumber = step.step_position || index + 1;

  return (
    <div className="collapsable-content">
      <button
        className="collapsable-content__header btn w-100"
        type="button"
        style={{ cursor: workInstructions.length === 0 ? 'default' : 'pointer' }}
        onClick={toggleDetails}
      >
        <div className="collapsable-content__header-inner">
          <div className="word-break font-bold text-left">
            {stepNumber}. {workstationName || 'N/A'}
          </div>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <span className={clsx('status-badge status-badge--rounded',
              `status-badge--${PRINT_STATUSES_TO_VARIANT[status]}` )}
            >
              {status || 'N/A'}
            </span>
            <span className="status-badge status-badge--info status-badge--rounded">
              { workInstructions.length }
            </span>
          </div>
        </div>
        <div className="d-flex align-items-center" style={{ width: '26px' }}>
          {workInstructions.length > 0 && (
            <FontAwesomeIcon
              icon={showDetails ? faChevronUp : faChevronDown}
              className="right mr-1 collapsable-title__icon"
            />
          )}
        </div>
      </button>
      {showDetails && workInstructions.length > 0 && (
        <div className="collapsable-content__body">
          {workInstructions.map((instruction, i) => (
            <WorkflowItem
              key={instruction.id || index}
              instruction={instruction}
              report={latestReportsByInstruction[instruction.uuid] || []}
              documents={documentList}
              users={userList}
              stepNumber={stepNumber}
              index={i}
              onPreview={setViewerFile}
            />
          ))}
        </div>
      )}
      { viewerFile && (
        <DocumentViewerModal
          isOpen={!!viewerFile}
          url={viewerFile.content || null}
          name={viewerFile.name || 'Document'}
          onClose={() => setViewerFile(null)}
        />
      )}
    </div>
  );
};

WorkflowStep.propTypes = {
  status: PropTypes.string.isRequired,
  stepID: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  checkListLinkings: PropTypes.object,
  report: PropTypes.array,
};

export default WorkflowStep;
